import clsx from 'clsx'
import moment from 'moment'
import { rem } from 'polished'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

import TextButton from 'LuxKit/Button/TextButton'
import LineArrowRightIcon from 'LuxKit/Icons/line/LineArrowRightIcon'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import BodyText from 'LuxKit/Typography/BodyText'
import type { AccommodationMobileDayCellSelectHandler } from 'components/AccommodationKit/AccommodationCalendar/AccommodationCalendar.types'
import HorizontalContainerWithFadedOverflow from 'components/Common/HorizontalContainerWithFadedOverflow'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import CalendarAvailabilityLegend from 'components/OfferPage/Filters/CalendarAvailabilityLegend'
import OfferCalendarDurationSelect from 'components/OfferPage/Filters/CalendarPicker/CalendarDuration/OfferCalendarDurationSelect'
import OfferDatePicker from 'components/OfferPage/Filters/DatePicker/OfferDatePicker'
import { PropertyPromotionNudgeBanner } from 'components/OfferPage/Property/PropertyPromotionNudgeBanner'
import Group from 'components/utils/Group'
import { INTL_SHORT_DAY_NAME_DAY_MONTH, ISO_DATE_FORMAT } from 'constants/dateFormats'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import {
  buildAvailabilityVariantMap,
  getAlternativeDateOccupancyRooms,
  getAlternativeDatesDuration,
} from 'hooks/OfferPage/alternativeDatesUtils'
import { useAlternativeDateSelection } from 'hooks/OfferPage/useAlternativeDateSelection'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import type { FeaturedDateRange } from 'lib/datetime/calendarDayViews'
import { useIntlDateFormatter } from 'lib/datetime/dateUtils'
import noop from 'lib/function/noop'
import OfferCalendarLuxPlusToggle from 'luxPlus/components/OfferCalendarLuxPlusToggle'
import { hasCalendarDayLuxPlusPrices, hasCalendarDayPrices } from 'selectors/offerPage/propertyOfferPageSelectors'
import eases from 'styles/tools/eases'

const CalendarDatePickerWrapper = styled.div`
  padding-top: ${rem(20)};
`

const FooterContents = styled.div`
  position: absolute;
  bottom: 0;
  inset-inline: 0;
  transform: translateY(100%);
  transition: transform 0.3s ${eases.expo.in};

  &.visible {
    position: unset;
    transform: translateY(0);
    transition: transform 0.3s ${eases.expo.out};
  }
`

interface Props {
  propertyTimezone: string | undefined
  propertyPromotion: App.Property.PropertyPromotion | undefined
  hasDayPrices: boolean
  hasDayLuxPlusPrices: boolean
  checkIn: string | undefined
  checkOut: string | undefined
  duration: number
  rooms: Array<App.Occupants>
  isAlternativeDatesFlow: boolean
  availabilityOfferId: string | undefined
  availabilityLoading: boolean
  availabilityData: Record<string, boolean> | undefined
  onBack?: () => void
  onClose?: () => void
}

function OfferSidebarDatePicker(props: Props) {
  const {
    propertyTimezone,
    propertyPromotion,
    hasDayPrices,
    hasDayLuxPlusPrices,
    checkIn,
    checkOut,
    duration,
    rooms,
    isAlternativeDatesFlow,
    availabilityOfferId,
    availabilityLoading,
    availabilityData,
    onBack,
    onClose = noop,
  } = props

  const isFixedLosFlow = !isAlternativeDatesFlow && propertyPromotion?.type === 'dynamicFlash'
  // The indicative 1 night calendar carries no promotion copy or day icons, only its fine print.
  const isIndicativePriceCalendar = hasDayPrices && !isAlternativeDatesFlow && !isFixedLosFlow
  const visiblePromotion = isIndicativePriceCalendar ? undefined : propertyPromotion

  const [dates, setDates] = useState<{ checkIn?: string, checkOut?: string }>({
    checkIn,
    checkOut,
  })

  useEffect(() => {
    // If we're in the fixed LoS flow and the duration has changed, update the check-out date.
    if (isFixedLosFlow && dates.checkIn) {
      setDates({ checkIn: dates.checkIn, checkOut: moment(dates.checkIn).add(duration, 'days').format(ISO_DATE_FORMAT) })
    }
  }, [dates.checkIn, duration, isFixedLosFlow])

  const intl = useIntl()
  const dispatch = useOfferPageDispatch()
  const intlDateFormatter = useIntlDateFormatter()
  const modalContext = useModalElementContext()

  const hasDaysSelected = !!(dates.checkIn && dates.checkOut)

  const onResetDates = useCallback(() => {
    setDates({ checkIn: undefined, checkOut: undefined })
  }, [])

  const onApply = useCallback(() => {
    if (!(dates.checkIn && !dates.checkOut)) {
      // only update state if it's not an 'invalid' (has check in, but not check out) selection
      dispatch({ type: OfferPageActions.setDates, checkIn: dates.checkIn, checkOut: dates.checkOut })
    }
    modalContext.resolve()
    if (isAlternativeDatesFlow) {
      dispatch({ type: OfferPageActions.setAlternativeDatesFlow, isAlternativeDatesFlow: false })
    }
  }, [dates.checkIn, dates.checkOut, dispatch, modalContext, isAlternativeDatesFlow])

  const onCloseModal = useCallback(() => {
    if (!(dates.checkIn && !dates.checkOut)) {
      dispatch({ type: OfferPageActions.setDates, checkIn: dates.checkIn, checkOut: dates.checkOut })
    }
    onClose()
    modalContext.resolve()
    if (isAlternativeDatesFlow) {
      dispatch({ type: OfferPageActions.setAlternativeDatesFlow, isAlternativeDatesFlow: false })
    }
  }, [dispatch, dates.checkIn, dates.checkOut, onClose, modalContext, isAlternativeDatesFlow])

  const scrollerRef = useRef<HTMLDivElement>(null)

  const featuredDateRanges = useMemo(() => {
    if (isAlternativeDatesFlow || !visiblePromotion) return
    const type = visiblePromotion.type === 'dynamicFlash' ? 'exclusive' : 'special'
    return [{
      type,
      startIsoDate: visiblePromotion.travelFromIsoDate,
      endIsoDate: visiblePromotion.travelToIsoDate,
    }] as Utils.NonEmptyArray<FeaturedDateRange>
  }, [isAlternativeDatesFlow, visiblePromotion])

  const occupancyRooms = useMemo(() => getAlternativeDateOccupancyRooms(rooms), [rooms])
  const onAlternativeDateRangeChange = useCallback((startDate: string, endDate: string | undefined) => {
    setDates({ checkIn: startDate, checkOut: endDate })
  }, [])
  const onAlternativeDateClick = useAlternativeDateSelection({
    startDate: dates.checkIn,
    endDate: dates.checkOut,
    offerId: availabilityOfferId,
    rooms: occupancyRooms,
    onDateRangeChange: onAlternativeDateRangeChange,
  })
  const alternativeDatesDuration = useMemo(
    () => getAlternativeDatesDuration(dates.checkIn, dates.checkOut, duration),
    [dates.checkIn, dates.checkOut, duration],
  )
  const availabilityVariantMap = useMemo(() => {
    if (!isAlternativeDatesFlow || !availabilityData) return undefined
    return buildAvailabilityVariantMap(availabilityData)
  }, [isAlternativeDatesFlow, availabilityData])
  const selectedMonth = (isAlternativeDatesFlow && checkIn) ? checkIn : undefined

  const onDatesChange = useCallback<AccommodationMobileDayCellSelectHandler>(day => {
    if (isAlternativeDatesFlow) {
      onAlternativeDateClick(day)
      return
    }
    if (isFixedLosFlow) {
      setDates({ checkIn: day.date, checkOut: moment(day.date).add(duration, 'days').format(ISO_DATE_FORMAT) })
      return
    }
    if (dates.checkIn && !dates.checkOut && moment(day.date).isAfter(dates.checkIn)) {
      setDates({ checkIn: dates.checkIn, checkOut: day.date })
    } else {
      setDates({ checkIn: day.date, checkOut: undefined })
    }
  }, [dates.checkIn, dates.checkOut, duration, isAlternativeDatesFlow, isFixedLosFlow, onAlternativeDateClick])
  const onExtendStay = useCallback((newCheckOutIsoDate: string) => {
    if (!dates.checkIn || dates.checkIn >= newCheckOutIsoDate) return
    setDates({ checkIn: dates.checkIn, checkOut: newCheckOutIsoDate })
  }, [dates.checkIn])

  const modalTitle = !isFixedLosFlow
    ? intl.formatMessage({
        id: 'wUSbhnHFFPLpmEWHWqgE',
        defaultMessage: 'Select dates',
        description: 'Heading title for the date picker modal on the offer page sidebar, in which users select a check-in date followed by a check-out date',
      })
    : intl.formatMessage({
        id: 'Cz-Ck1Jg7CVsu4LwLUPS',
        defaultMessage: 'Select check-in date',
        description: 'Heading title for the date picker modal on the offer page sidebar, in which users select a check-in date for a fixed length of stay',
      })

  return (<>
    <ModalHeader
      title={modalTitle}
      onBackButtonClick={onBack}
      onCloseButtonClick={onCloseModal}
    >
      <Group direction="vertical" gap={20}>
        {isFixedLosFlow && <HorizontalContainerWithFadedOverflow overflowGutter={20} gap={8}>
          <OfferCalendarDurationSelect variant="modal" />
          {hasDayLuxPlusPrices && <OfferCalendarLuxPlusToggle />}
        </HorizontalContainerWithFadedOverflow>}
        {isAlternativeDatesFlow && <CalendarAvailabilityLegend duration={alternativeDatesDuration} />}
        {!isAlternativeDatesFlow && visiblePromotion && <PropertyPromotionNudgeBanner
          promotion={visiblePromotion}
          checkInIsoDate={dates.checkIn}
          checkOutIsoDate={dates.checkOut}
          onExtendStay={!isFixedLosFlow ? onExtendStay : undefined}
          canShowConfirmation
        />}
      </Group>
    </ModalHeader>
    <ModalBody ref={scrollerRef}>
      <ModalContent>
        <CalendarDatePickerWrapper>
          <LoadingIndicator floating visible={isAlternativeDatesFlow && availabilityLoading} />
          <OfferDatePicker
            scrollerRef={scrollerRef}
            size="small"
            propertyTimezone={propertyTimezone}
            featuredDateRanges={featuredDateRanges}
            checkInDate={dates.checkIn}
            checkOutDate={dates.checkOut}
            onDatesChange={onDatesChange}
            availabilityVariantMap={availabilityVariantMap}
            selectedMonth={selectedMonth}
            renderFromStart={isAlternativeDatesFlow}
            isIndicativePriceCalendar={isIndicativePriceCalendar}
          />
        </CalendarDatePickerWrapper>
      </ModalContent>
    </ModalBody>
    <FooterContents className={clsx({ visible: hasDaysSelected })}>
      <ModalFooter>
        <VerticalSpacer gap={8}>
          {dates.checkIn && dates.checkOut && <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="center">
            <BodyText variant="medium">
              <Group direction="horizontal" verticalAlign="center">
                <span>{intlDateFormatter(dates.checkIn, INTL_SHORT_DAY_NAME_DAY_MONTH)}</span>
                <LineArrowRightIcon size="XS" />
                <span>{intlDateFormatter(dates.checkOut, INTL_SHORT_DAY_NAME_DAY_MONTH)}</span>
              </Group>
            </BodyText>
            <TextButton kind="tertiary" horizontalOutdent="end" onClick={onResetDates}>
              {intl.formatMessage({
                id: '7SzKCvRhP6Tb2Jqq_d-W',
                defaultMessage: 'Reset',
                description: 'Button text to reset selected dates in sidebar date picker',
              })}
            </TextButton>
          </Group>}
          <Group direction="horizontal" horizontalAlign="space-between">
            <TextButton kind="primary" size="large" type="submit" fit="mobile-full-width" onClick={onApply}>
              {intl.formatMessage({
                id: 'byRjBo7yRMtPGjfs8taE',
                defaultMessage: 'Apply',
                description: 'Button text to apply selected dates in sidebar date picker',
              })}
            </TextButton>
          </Group>
        </VerticalSpacer>
      </ModalFooter>
    </FooterContents>
  </>)
}

type MappedProps = Pick<Props,
  | 'propertyTimezone'
  | 'propertyPromotion'
  | 'hasDayPrices'
  | 'hasDayLuxPlusPrices'
  | 'checkIn'
  | 'checkOut'
  | 'duration'
  | 'rooms'
  | 'isAlternativeDatesFlow'
  | 'availabilityOfferId'
  | 'availabilityLoading'
  | 'availabilityData'
>

export default withOfferPageAndReduxState((state, appState): MappedProps => {
  const availabilityOfferId = state.offer?.id
  const availabilityEntry = availabilityOfferId ? appState.calendar.availabilityDateRange[availabilityOfferId] : undefined
  return {
    propertyTimezone: state.property?.location.timezone ?? state.offer?.property.timezone ?? state.bedbankOffer?.property.timezone,
    propertyPromotion: state.property?.promotion,
    hasDayPrices: hasCalendarDayPrices(state, appState),
    hasDayLuxPlusPrices: hasCalendarDayLuxPlusPrices(state, appState),
    checkIn: state.checkInDate,
    checkOut: state.checkOutDate,
    duration: state.duration,
    rooms: state.rooms,
    isAlternativeDatesFlow: !!state.isAlternativeDatesFlow,
    availabilityOfferId,
    availabilityLoading: !!availabilityEntry?.fetching,
    availabilityData: availabilityEntry?.data,
  }
}, OfferSidebarDatePicker)
